import { Button, cn } from "@tellescope/design-system";
import { CloudUpload } from "lucide-react";
import { BlockProps } from "../../../types";
import { useMedicationsBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { MedicationsRecordsList } from "./MedicationsRecordsList";
import { MedicationUploadForm } from "./MedicationUploadForm";
import { MedicationDetails } from "./MedicationDetails";
import { useTranslation } from "react-i18next";
import { BlockEmptyState } from "../BlockEmptyState";

export const MedicationsBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  ...rest
}: BlockProps) => {
  const { t } = useTranslation("medications");
  const {
    parsedInfo,
    enduser,
    showUploadForm,
    openUploadForm,
    closeUploadForm,
    selectedMedication,
    openMedication,
    closeMedication,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canCreateBlock,
    canDeleteBlock,
  } = useMedicationsBlock({ ...rest, onMaximize, alwaysShowButtons, className });

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          editMode &&
          !showUploadForm && (
            <Button
              iconOnly
              variant="ghost"
              size="sm"
              color="muted"
              hoverLabel={t("Upload Medications")}
              onClick={openUploadForm}
            >
              <CloudUpload />
            </Button>
          )
        }
      />

      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col", contentClassName)}
      >
        {!enduser ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("Loading medications...")}</BlockEmptyState>
          </div>
        ) : showUploadForm ? (
          <MedicationUploadForm contact={enduser} onUpload={closeUploadForm} />
        ) : selectedMedication ? (
          <MedicationDetails medication={selectedMedication} onBack={closeMedication} />
        ) : (
          <MedicationsRecordsList contact={enduser} showDetails={expanded} onSelectMedication={openMedication} />
        )}
      </BlockContent>
    </div>
  );
};
